import Link from 'next/link';
import styled, { css } from 'styled-components';
import {
  Button,
  Eyebrow,
  HeroBackground,
  HeroTitle,
  Lead,
  SectionContainer,
} from '@/components/ui';
import { up } from '@/themes';

const Wrapper = styled.section`
  isolation: isolate;
  overflow: hidden;
  padding-bottom: 48px;
  padding-top: 56px;
  position: relative;

  ${up.sm} {
    padding-bottom: 72px;
    padding-top: 88px;
  }
`;

const Content = styled.div`
  max-width: 768px;

  ${({ $align }) =>
    $align === 'center' &&
    css`
      margin-left: auto;
      margin-right: auto;
      text-align: center;
    `};
`;

const EyebrowLink = styled(Link)`
  color: inherit;

  &:hover {
    text-decoration: underline;
    text-underline-offset: 4px;
  }
`;

const Title = styled(HeroTitle)`
  margin-top: 16px;
`;

const Description = styled(Lead)`
  margin-top: 24px;
  max-width: 672px;

  ${({ $align }) =>
    $align === 'center' &&
    css`
      margin-left: auto;
      margin-right: auto;
    `};
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 32px;

  ${up.sm} {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: ${({ $align }) => ($align === 'center' ? 'center' : 'flex-start')};
  }
`;

/**
 * Hero for a landing page: eyebrow, the page's single h1, lead and actions on
 * the grid background.
 *
 *   <PageHero
 *     eyebrow={t('eyebrow')}
 *     eyebrowHref="/currency"
 *     title={t('title')}
 *     description={t('lead')}
 *     actions={[{ label: t('browse'), href: '/buy' }, { label: t('all'), href: '/currency' }]}
 *   />
 *
 * `actions`: `[{ label, href, variant? }]`; the first is the filled pill, the
 * rest are outlined. `children` render under the actions.
 */
const PageHero = ({
  actions = [],
  align = 'left',
  children,
  className,
  description,
  eyebrow,
  eyebrowHref,
  title,
}) => (
  <Wrapper className={className} data-section="hero">
    <HeroBackground />
    <SectionContainer>
      <Content $align={align}>
        {eyebrow && (
          <Eyebrow>
            {eyebrowHref ? <EyebrowLink href={eyebrowHref}>{eyebrow}</EyebrowLink> : eyebrow}
          </Eyebrow>
        )}
        <Title>{title}</Title>
        {description && (
          <Description $large $align={align}>
            {description}
          </Description>
        )}
        {actions.length > 0 && (
          <Actions $align={align}>
            {actions.map(({ label, variant, ...action }, index) => (
              <Button
                key={label}
                $size="pill"
                $variant={variant || (index === 0 ? 'default' : 'outline')}
                {...action}
              >
                {label}
              </Button>
            ))}
          </Actions>
        )}
        {children}
      </Content>
    </SectionContainer>
  </Wrapper>
);

export default PageHero;
